import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const RIFT_ASSET_TIMEOUT = 5000;
const riftStylesheetPromises = new Map();
const riftModulePromises = new Map();

/** @param {string} assetUrl */
function getAbsoluteAssetUrl(assetUrl) {
  return assetUrl ? new URL(assetUrl, window.location.href).href : '';
}

/** @param {string} href */
function loadRiftStylesheet(href) {
  const absoluteHref = getAbsoluteAssetUrl(href);
  if (!absoluteHref) return Promise.resolve();

  const cachedPromise = riftStylesheetPromises.get(absoluteHref);
  if (cachedPromise) return cachedPromise;

  const existingStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
    (link) => link instanceof HTMLLinkElement && link.href === absoluteHref
  );

  const stylesheet =
    existingStylesheet instanceof HTMLLinkElement ? existingStylesheet : document.createElement('link');

  const loadPromise = new Promise((resolve, reject) => {
    if (stylesheet.sheet) {
      resolve();
      return;
    }

    const timeout = window.setTimeout(
      () => reject(new Error(`Stylesheet timed out: ${absoluteHref}`)),
      RIFT_ASSET_TIMEOUT
    );
    const finish = (callback) => {
      window.clearTimeout(timeout);
      stylesheet.removeEventListener('load', handleLoad);
      stylesheet.removeEventListener('error', handleError);
      callback();
    };
    const handleLoad = () => finish(resolve);
    const handleError = () => finish(() => reject(new Error(`Stylesheet failed: ${absoluteHref}`)));

    stylesheet.addEventListener('load', handleLoad, { once: true });
    stylesheet.addEventListener('error', handleError, { once: true });

    if (!existingStylesheet) {
      stylesheet.rel = 'stylesheet';
      stylesheet.href = absoluteHref;
      stylesheet.dataset.riftQuickAddStylesheet = '';
      document.head.append(stylesheet);
    }
  }).catch((error) => {
    riftStylesheetPromises.delete(absoluteHref);
    if (!existingStylesheet) stylesheet.remove();
    throw error;
  });

  riftStylesheetPromises.set(absoluteHref, loadPromise);
  return loadPromise;
}

/** @param {string} src */
function loadRiftModule(src) {
  const absoluteSrc = getAbsoluteAssetUrl(src);
  if (!absoluteSrc) return Promise.resolve();

  const cachedPromise = riftModulePromises.get(absoluteSrc);
  if (cachedPromise) return cachedPromise;

  let timeout = 0;
  const loadPromise = Promise.race([
    import(absoluteSrc),
    new Promise((_, reject) => {
      timeout = window.setTimeout(
        () => reject(new Error(`Module timed out: ${absoluteSrc}`)),
        RIFT_ASSET_TIMEOUT
      );
    }),
  ])
    .finally(() => window.clearTimeout(timeout))
    .catch((error) => {
      riftModulePromises.delete(absoluteSrc);
      throw error;
    });
  riftModulePromises.set(absoluteSrc, loadPromise);
  return loadPromise;
}

/**
 * Loads only theme-owned RIFT assets declared by known product components.
 * @param {Element} root
 */
async function hydrateRiftQuickAddFeatures(root) {
  const criticalAssets = [];
  const optionalAssets = [];

  for (const variantPicker of root.querySelectorAll(
    'variant-picker[data-rift-variant-media-stylesheet-href]'
  )) {
    if (!(variantPicker instanceof HTMLElement)) continue;
    criticalAssets.push(loadRiftStylesheet(variantPicker.dataset.riftVariantMediaStylesheetHref || ''));
    if (variantPicker.dataset.riftVariantMediaModuleSrc) {
      criticalAssets.push(loadRiftModule(variantPicker.dataset.riftVariantMediaModuleSrc));
    }
  }

  for (const personalization of root.querySelectorAll(
    'rift-product-personalization[data-rift-personalization-active="true"]'
  )) {
    if (!(personalization instanceof HTMLElement)) continue;
    criticalAssets.push(loadRiftStylesheet(personalization.dataset.riftStylesheetHref || ''));
    criticalAssets.push(loadRiftModule(personalization.dataset.riftModuleSrc || ''));
  }

  for (const sizeGuide of root.querySelectorAll('rift-size-guide')) {
    if (!(sizeGuide instanceof HTMLElement)) continue;
    optionalAssets.push(loadRiftStylesheet(sizeGuide.dataset.riftStylesheetHref || ''));
    optionalAssets.push(loadRiftModule(sizeGuide.dataset.riftModuleSrc || ''));
  }

  void Promise.allSettled(optionalAssets).then((optionalResults) => {
    for (const result of optionalResults) {
      if (result.status === 'rejected') {
        console.warn('[quick-add] An optional RIFT feature could not be loaded.', result.reason);
      }
    }
  });

  await Promise.all(criticalAssets);
}

/**
 * Returns only active, outermost conditional groups so nested content stays intact.
 * @param {Document | Element} root
 * @param {string} [productId]
 * @returns {HTMLElement[]}
 */
function getActiveRiftConditionalGroups(root, productId = '') {
  const normalizedProductId = productId ? String(productId) : '';

  return Array.from(
    root.querySelectorAll('[data-rift-conditional-group][data-rift-conditional-active="true"]')
  ).filter((group) => {
    if (!(group instanceof HTMLElement)) return false;
    if (group.parentElement?.closest('[data-rift-conditional-group]')) return false;
    return (
      !normalizedProductId ||
      !group.dataset.riftConditionalProductId ||
      group.dataset.riftConditionalProductId === normalizedProductId
    );
  });
}

/**
 * Keeps conditional display content aligned with the selected Combined Listing product.
 * @param {Element} root
 * @param {Document | Element} newHtml
 * @param {string} productId
 */
function syncRiftQuickAddConditionalGroups(root, newHtml, productId) {
  const currentGroups = getActiveRiftConditionalGroups(root);
  const sourceGroups = getActiveRiftConditionalGroups(newHtml, productId);
  const currentByBlockId = new Map(
    currentGroups.map((group) => [group.dataset.riftConditionalBlockId || '', group])
  );
  const insertionReference =
    root.querySelector('.buy-buttons-block') || root.querySelector('product-form-component');

  for (const sourceGroup of sourceGroups) {
    const blockId = sourceGroup.dataset.riftConditionalBlockId || '';
    const currentGroup = currentByBlockId.get(blockId);
    const nextGroup = /** @type {HTMLElement} */ (sourceGroup.cloneNode(true));

    if (currentGroup) {
      currentGroup.replaceWith(nextGroup);
      currentByBlockId.delete(blockId);
    } else if (insertionReference) {
      insertionReference.before(nextGroup);
    } else {
      root.append(nextGroup);
    }
  }

  for (const staleGroup of currentByBlockId.values()) staleGroup.remove();
}

/** @param {Element} root */
function prepareQuickAddFormIdentity(root) {
  const forms = root.querySelectorAll('product-form-component form[id]');

  forms.forEach((form, index) => {
    if (!(form instanceof HTMLFormElement)) return;

    const currentId = form.getAttribute('id') || '';
    if (!currentId) return;
    const quickAddId = `${currentId}-QuickAdd${index > 0 ? `-${index + 1}` : ''}`;
    form.setAttribute('id', quickAddId);

    for (const control of root.querySelectorAll('[form]')) {
      if (control.getAttribute('form') === currentId) control.setAttribute('form', quickAddId);
    }

    for (const personalization of root.querySelectorAll('rift-product-personalization[data-rift-form-id]')) {
      if (!(personalization instanceof HTMLElement)) continue;
      if (personalization.dataset.riftFormId === currentId) personalization.dataset.riftFormId = quickAddId;
    }
  });

  for (const [index, personalization] of Array.from(
    root.querySelectorAll('rift-product-personalization')
  ).entries()) {
    rekeyRiftPersonalizationIds(personalization, `QuickAdd${index > 0 ? `-${index + 1}` : ''}`);
  }
}

/**
 * Prevents the Quick Add copy from duplicating field and ARIA IDs from the product page.
 * @param {Element} personalization
 * @param {string} suffix
 */
function rekeyRiftPersonalizationIds(personalization, suffix = 'QuickAdd') {
  const idMap = new Map();

  for (const element of personalization.querySelectorAll('[id]')) {
    const currentId = element.id;
    if (!currentId) continue;
    const nextId = `${currentId}-${suffix}`;
    idMap.set(currentId, nextId);
    element.id = nextId;
  }

  const referenceAttributes = ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby'];
  for (const element of personalization.querySelectorAll(referenceAttributes.map((name) => `[${name}]`).join(','))) {
    for (const attribute of referenceAttributes) {
      const value = element.getAttribute(attribute);
      if (!value) continue;

      const nextValue = value
        .split(/\s+/)
        .map((id) => idMap.get(id) || id)
        .join(' ');
      element.setAttribute(attribute, nextValue);
    }
  }
}

/**
 * Associates one personalization component with a specific Product Form instance.
 * @param {Element} personalization
 * @param {string} formId
 */
function bindRiftPersonalizationToForm(personalization, formId) {
  if (!(personalization instanceof HTMLElement) || !formId) return;

  personalization.dataset.riftFormId = formId;
  for (const control of personalization.querySelectorAll('[form]')) {
    control.setAttribute('form', formId);
  }
}

/**
 * Associates every active personalization instance with the final Product Form in Quick Add.
 * @param {Element} root
 * @returns {HTMLElement[]}
 */
function bindRiftQuickAddPersonalizations(root) {
  const personalizations = Array.from(
    root.querySelectorAll('rift-product-personalization[data-rift-personalization-active="true"]')
  ).filter((personalization) => personalization instanceof HTMLElement);
  const productFormComponents = Array.from(root.querySelectorAll('product-form-component'));
  const forms = Array.from(root.querySelectorAll('product-form-component form[id]')).filter(
    (form) => form instanceof HTMLFormElement
  );

  for (const personalization of personalizations) {
    const configuredFormId = personalization.dataset.riftFormId || '';
    let productForm = forms.find((form) => form.getAttribute('id') === configuredFormId);

    if (!productForm) {
      const productId = personalization.dataset.riftProductId || '';
      const matchingComponent = productFormComponents.find(
        (component) => component instanceof HTMLElement && component.dataset.productId === productId
      );
      const matchingForm = matchingComponent?.querySelector('form[id]');
      if (matchingForm instanceof HTMLFormElement) productForm = matchingForm;
    }

    if (!productForm && forms.length === 1) productForm = forms[0];
    if (!(productForm instanceof HTMLFormElement)) {
      throw new Error('Quick Add Product Form is unavailable for personalization.');
    }

    bindRiftPersonalizationToForm(personalization, productForm.getAttribute('id') || '');
  }

  return personalizations;
}

/**
 * Loads RIFT assets, then refreshes form bindings after the final Quick Add DOM exists.
 * @param {Element} root
 */
async function activateRiftQuickAddFeatures(root) {
  const personalizations = bindRiftQuickAddPersonalizations(root);
  await hydrateRiftQuickAddFeatures(root);

  if (personalizations.length === 0) return;
  await customElements.whenDefined('rift-product-personalization');

  for (const personalization of personalizations) {
    if (typeof personalization.refresh !== 'function' || !personalization.refresh()) {
      throw new Error('Quick Add personalization could not connect to its Product Form.');
    }

    const formId = personalization.dataset.riftFormId || '';
    const form = Array.from(root.querySelectorAll('form[id]')).find(
      (candidate) => candidate.getAttribute('id') === formId
    );
    const controls = Array.from(
      personalization.querySelectorAll('[data-rift-personalization-control]:not(:disabled)')
    );
    if (
      !(form instanceof HTMLFormElement) ||
      controls.length === 0 ||
      controls.some((control) => control.form !== form)
    ) {
      throw new Error('Quick Add personalization controls are not associated with the active Product Form.');
    }
  }
}

/**
 * Replaces personalization when a Combined Listing selects another product.
 * @param {Element} root
 * @param {Document | Element} newHtml
 * @param {string} productId
 */
function syncRiftQuickAddPersonalization(root, newHtml, productId) {
  const currentPersonalizations = Array.from(root.querySelectorAll('rift-product-personalization'));
  const sourcePersonalizations = Array.from(
    newHtml.querySelectorAll('rift-product-personalization[data-rift-personalization-active="true"]')
  );
  const sourcePersonalization =
    sourcePersonalizations.find(
      (personalization) =>
        personalization instanceof HTMLElement && personalization.dataset.riftProductId === productId
    ) || sourcePersonalizations[0];

  if (!sourcePersonalization) {
    currentPersonalizations.forEach((personalization) => personalization.remove());
    return;
  }

  const productForm = root.querySelector('product-form-component form[id]');
  if (!(productForm instanceof HTMLFormElement)) {
    throw new Error('Quick Add Product Form is unavailable for personalization.');
  }

  const nextPersonalization = /** @type {Element} */ (sourcePersonalization.cloneNode(true));
  bindRiftPersonalizationToForm(nextPersonalization, productForm.getAttribute('id') || '');
  rekeyRiftPersonalizationIds(nextPersonalization);

  const currentPersonalization = currentPersonalizations.shift();
  if (currentPersonalization) {
    currentPersonalization.replaceWith(nextPersonalization);
  } else {
    const insertionReference = productForm.closest('.buy-buttons-block') || productForm.closest('product-form-component');
    insertionReference?.insertAdjacentElement('beforebegin', nextPersonalization);
  }

  currentPersonalizations.forEach((personalization) => personalization.remove());
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      const didUpdate = await this.updateQuickAddModal(freshContent);
      if (!didUpdate) {
        if (currentUrl) window.location.href = currentUrl;
        return;
      }
      this.#updateVariantPicker(productGrid);
    } else {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const personalizations = Array.from(
        productGrid.querySelectorAll('rift-product-personalization[data-rift-personalization-active="true"]')
      );
      const conditionalGroups = getActiveRiftConditionalGroups(productGrid);
      const sizeGuide = productGrid.querySelector('rift-size-guide');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      const mobileContentNodes = [
        variantPicker,
        ...conditionalGroups,
        ...personalizations,
        sizeGuide,
        productFormComponent,
      ]
        .filter((node, index, nodes) => node instanceof Element && nodes.indexOf(node) === index)
        .filter(
          (node) =>
            !conditionalGroups.some(
              (conditionalGroup) => conditionalGroup !== node && conditionalGroup.contains(node)
            )
        )
        .sort((left, right) =>
          left.compareDocumentPosition(right) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        );

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      for (const contentNode of mobileContentNodes) productGrid.appendChild(contentNode);

      productDetails?.remove();
    }

    prepareQuickAddFormIdentity(productGrid);

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    try {
      await activateRiftQuickAddFeatures(modalContent);
    } catch (error) {
      console.warn('[quick-add] Required RIFT product assets could not be loaded.', error);
      return false;
    }

    this.#syncVariantSelection(modalContent);
    return true;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (anchorElement) {
          if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
          if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
        }

        if (newProduct) {
          const modalContent = this.querySelector('#quick-add-modal-content');
          if (!modalContent) return;

          try {
            syncRiftQuickAddConditionalGroups(modalContent, html, newProduct.id);
            syncRiftQuickAddPersonalization(modalContent, html, newProduct.id);
            await activateRiftQuickAddFeatures(modalContent);
          } catch (error) {
            console.warn('[quick-add] The selected product requires the full product page.', error);
            window.location.href = newProduct.url;
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    for (const feature of this.querySelectorAll(
      'rift-size-guide, rift-product-personalization, [data-rift-conditional-group]'
    )) {
      feature.remove();
    }

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
